import { backendUrl } from './App';

export type DeliveryCostRequest = {
  partner_name_code: string;
  latitude: number;
  longitude: number;
  total_products_cost: number;
};

export type DeliveryCostResult = {
  cost: number;
  minimumOrderAmount: number;
};

const getNumericValueFromRecord = (record: Record<string, unknown>, keys: string[]): number | null => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsedValue = Number(value.replace(',', '.'));
      if (Number.isFinite(parsedValue)) return parsedValue;
    }
  }
  return null;
};

const extractDeliveryCostAmount = (payload: unknown): number | null => {
  if (typeof payload === 'number' && Number.isFinite(payload)) return payload;
  if (typeof payload === 'string') {
    const parsedValue = Number(payload.replace(',', '.'));
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return getNumericValueFromRecord(payload as Record<string, unknown>, [
      'delivery_price', 'delivery_cost', 'deliveryCost', 'cost', 'price', 'amount', 'result',
    ]);
  }
  return null;
};

const extractMinimumOrderAmount = (payload: unknown): number => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return 0;
  const amount = getNumericValueFromRecord(payload as Record<string, unknown>, [
    'minimum_order_amount', 'minimumOrderAmount',
  ]);
  return amount != null && amount > 0 ? amount : 0;
};

const extractErrorMessage = (payload: unknown, fallback: string): string => {
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const record = payload as Record<string, unknown>;
    const message = record.message ?? record.error ?? record.reason;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
};

/**
 * fetchDeliveryCost
 *
 * Centralizes courier-price retrieval for the customer checkout and the admin
 * delivery-request form. It accepts typed partner, coordinate, and product-total
 * inputs and returns a normalized cost while surfacing HTTP and payload errors.
 */
export async function fetchDeliveryCost(
  request: DeliveryCostRequest,
  labels: { failed: string; invalid: string },
): Promise<DeliveryCostResult> {
  const response = await fetch(`${backendUrl}/get_delivery_cost_by_partner`, {
    method: 'POST',
    headers: {
      'ngrok-skip-browser-warning': 'true',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) throw new Error(extractErrorMessage(payload, labels.failed));

  const cost = extractDeliveryCostAmount(payload);
  if (cost == null) throw new Error(extractErrorMessage(payload, labels.invalid));
  return { cost, minimumOrderAmount: extractMinimumOrderAmount(payload) };
}
